import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, MessageChatCircle, Phone01 } from "@opendate-app/setlist";
import "./styles/mySettingsStyles.css";

const axios = require("axios").default;

const POLL_INTERVAL_MS = 3000;

const smsHref = (phoneNumber, body) => `sms:${phoneNumber}?&body=${encodeURIComponent(body)}`;

const ConductorTextingCard = ({
  csrfToken,
  teamName,
  conductorPhoneNumber,
  conductorPhoneNumberDisplay,
  link: initialLink,
}) => {
  const [link, setLink] = useState(initialLink);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

  const awaitingVerification = Boolean(link && !link.verified && link.verification_text);

  useEffect(() => {
    if (!awaitingVerification) return undefined;

    const expiresAt = Date.parse(link.verification_code_expires_at);
    const timer = window.setInterval(() => {
      if (Date.now() > expiresAt) {
        window.clearInterval(timer);
        return;
      }
      axios.get("/conductor_phone_link")
        .then((response) => {
          if (response.data.verified) setLink(response.data);
        })
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [awaitingVerification, link && link.verification_code_expires_at]);

  const request = (promise, onSuccess, failureMessage) => {
    setBusy(true);
    setError(null);
    promise
      .then(onSuccess)
      .catch(() => setError(failureMessage))
      .then(() => setBusy(false));
  };

  const issueCode = () => request(
    axios.post("/conductor_phone_link"),
    (response) => setLink(response.data),
    "We could not create a code. Try again in a little while."
  );

  const disconnect = () => request(
    axios.delete("/conductor_phone_link"),
    () => setLink(null),
    "We could not disconnect your phone."
  );

  const renderBody = () => {
    if (link && link.verified) {
      return (
        <ul className="settings-addr-list">
          <li className="settings-addr-row">
            <span className="settings-addr-main">
              <span className="settings-addr-top">
                <span className="settings-addr-email">Phone ending in {link.phone_number_last_four}</span>
                <span className="settings-status settings-status--success">Connected</span>
              </span>
              <span className="settings-addr-meta">
                Text {conductorPhoneNumberDisplay} any time to ask Conductor about {teamName}. Text STOP to disconnect.
              </span>
            </span>
            <span className="settings-addr-actions">
              <Button variant="secondary" disabled={busy} onClick={disconnect}>
                Disconnect
              </Button>
            </span>
          </li>
        </ul>
      );
    }

    if (link && link.verification_text) {
      return (
        <div className="settings-field">
          <p className="settings-label">
            From your phone, text <strong>{link.verification_text}</strong> to <strong>{conductorPhoneNumberDisplay}</strong>.
          </p>
          <p className="settings-helper">
            The code works for 15 minutes. Conductor texts you back when your phone is connected.
          </p>
          <div className="settings-addr-actions">
            <Button as="a" href={smsHref(conductorPhoneNumber, link.verification_text)} variant="primary" icon={MessageChatCircle}>
              Open Messages
            </Button>
            <Button variant="secondary" disabled={busy} onClick={issueCode}>
              New code
            </Button>
          </div>
        </div>
      );
    }

    return (
      <Button variant="primary" icon={Phone01} disabled={busy} onClick={issueCode}>
        Connect my phone
      </Button>
    );
  };

  return (
    <section className="settings-card" data-settings-section id="text-conductor">
      <div className="settings-card-header">
        <h2 className="settings-card-title">Text Conductor</h2>
        <p className="settings-card-desc">
          Ask Conductor about {teamName} by text message, from iMessage or SMS, with the same access you have in Opendate.
        </p>
      </div>
      <div className="settings-card-body">
        {renderBody()}
        {error ? <p className="settings-field-error" role="alert">{error}</p> : null}
      </div>
    </section>
  );
};

ConductorTextingCard.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  teamName: PropTypes.string.isRequired,
  conductorPhoneNumber: PropTypes.string.isRequired,
  conductorPhoneNumberDisplay: PropTypes.string.isRequired,
  link: PropTypes.shape({
    id: PropTypes.number.isRequired,
    verified: PropTypes.bool.isRequired,
    phone_number_last_four: PropTypes.string,
    verification_text: PropTypes.string,
  }),
};

ConductorTextingCard.defaultProps = {
  link: null,
};

export default ConductorTextingCard;
